import { type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { z, type ZodType } from 'zod';

import { parseOrThrow } from '../api/contract.js';
import { useApiMutation, useApiQuery } from '../api/hooks.js';
import { queryKeys } from '../api/query-keys.js';

/**
 * A module's API layer, from its base path.
 *
 * Every module was writing the same three files: paths derived from one base,
 * a detail query, an options query, and three mutations differing only in verb.
 * What varied was the schema and the resource name. Those are the arguments.
 *
 * What is baked in is what each module was getting right by copying and would
 * eventually get wrong by hand: a fresh idempotency key per submission (the
 * Gateway retains keys for 24 hours, so a double-submitted form is one record
 * — and a *stable* key would make the second record a replay of the first),
 * invalidation of the list and detail prefixes but not the reference ones (so
 * saving a form does not refetch two hundred and fifty countries), and a
 * parsed response, because a write that answers an unexpected shape is the same
 * contract failure as a read that does.
 */

const createdSchema = z.object({ id: z.string() });

export type Created = z.infer<typeof createdSchema>;

export const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
  // Optional, because most modules declare no metaColumns and the key is then
  // absent rather than empty. A required field here would fail the parse for
  // every picker in the portal.
  meta: z.record(z.string(), z.string().nullable()).optional(),
});

export type ResourceOption = z.infer<typeof optionSchema>;

export interface ResourceModuleOptions<TDetail extends ZodType> {
  /** Cache root. Must be a key in `queryKeys`. */
  readonly resource: keyof typeof queryKeys;
  /** Gateway path, e.g. `/v1/properties`. */
  readonly basePath: string;
  readonly detail: TDetail;
}

export interface ResourceEndpoints {
  readonly list: string;
  readonly create: string;
  readonly options: string;
  readonly detail: (id: string) => string;
  readonly update: (id: string) => string;
  readonly delete: (id: string) => string;
}

export interface UpdateVariables<TValues> {
  readonly id: string;
  readonly payload: TValues;
}

export interface ResourceModule<TDetail extends ZodType> {
  readonly endpoints: ResourceEndpoints;
  readonly writtenKeys: readonly (readonly string[])[];
  readonly useItem: (id: string) => UseQueryResult<TDetail['_output']>;
  readonly useOptions: (q?: string) => UseQueryResult<readonly ResourceOption[]>;
  readonly useCreate: () => UseMutationResult<Created, unknown, Record<string, unknown>>;
  readonly useUpdate: () => UseMutationResult<
    unknown,
    unknown,
    UpdateVariables<Record<string, unknown>>
  >;
  readonly useSoftDelete: () => UseMutationResult<unknown, unknown, string>;
}

export function createResourceModule<TDetail extends ZodType>(
  options: ResourceModuleOptions<TDetail>,
): ResourceModule<TDetail> {
  const { resource, basePath, detail } = options;
  const keys = queryKeys[resource] as {
    lists: readonly string[];
    details: readonly string[];
    detail: (id: string) => readonly string[];
    options: (params: { q?: string | undefined }) => readonly string[];
  };

  const endpoints: ResourceEndpoints = {
    list: basePath,
    create: basePath,
    options: `${basePath}/options`,
    detail: (id) => `${basePath}/${encodeURIComponent(id)}`,
    update: (id) => `${basePath}/${encodeURIComponent(id)}`,
    delete: (id) => `${basePath}/${encodeURIComponent(id)}`,
  };

  const writtenKeys = [keys.lists, keys.details];

  return {
    endpoints,
    writtenKeys,

    useItem: (id) =>
      useApiQuery({
        queryKey: keys.detail(id),
        path: endpoints.detail(id),
        schema: detail,
        enabled: id !== '',
      }),

    useOptions: (q) =>
      useApiQuery({
        queryKey: keys.options({ q }),
        path: endpoints.options,
        schema: z.array(optionSchema),
        ...(q === undefined || q === '' ? {} : { query: { q } }),
      }),

    useCreate: () =>
      useApiMutation<Record<string, unknown>, Created>({
        mutationFn: async (api, payload) => {
          const answer = await api.post<unknown>(endpoints.create, payload, crypto.randomUUID());
          return parseOrThrow(createdSchema, answer, endpoints.create);
        },
        invalidate: writtenKeys,
      }),

    useUpdate: () =>
      useApiMutation<UpdateVariables<Record<string, unknown>>, unknown>({
        // PATCH, not PUT: a form owns part of a record, and the parts owned by
        // other screens must not be erased by omission here.
        mutationFn: async (api, { id, payload }) =>
          await api.patch<unknown>(endpoints.update(id), payload),
        invalidate: writtenKeys,
      }),

    useSoftDelete: () =>
      useApiMutation<string, unknown>({
        // Soft, always. Reservations, payments and audit entries still point at
        // the row; a booking whose record has vanished cannot be honoured.
        mutationFn: async (api, id) => await api.delete<unknown>(endpoints.delete(id)),
        invalidate: writtenKeys,
      }),
  };
}
